/**
 * RTWO Cryptography & Identity Engine — Scaffold Key Provider
 *
 * Base for key provider scaffolds without real cryptographic implementations.
 * All operations return null/false — no keys, signatures, or hashes fabricated.
 */

import type {
  CryptoAlgorithm,
  EllipticCurve,
  KeyPairId,
  MessageHashHex,
  PublicKeyHex,
  RTWOKeyPair,
} from '../domain-models';
import type { KeyPairGenerationRequest } from '../service-interfaces';
import type {
  AddressProviderResult,
  IKeyProvider,
  KeyPairProviderResult,
  KeyProviderStatus,
  KeyProviderType,
  RTWOKeyProviderDescriptor,
  SignatureProviderResult,
} from '../adapters/key-provider-interface';

export abstract class ScaffoldKeyProvider implements IKeyProvider {
  abstract readonly providerId: string;
  abstract readonly providerName: string;
  abstract readonly providerType: KeyProviderType;
  abstract readonly scaffoldMessage: string;

  protected status: KeyProviderStatus = 'not_configured';

  async connect(): Promise<void> {
    this.status = 'connecting';
    this.status = 'error';
  }

  async disconnect(): Promise<void> {
    this.status = 'disconnected';
  }

  getStatus(): KeyProviderStatus {
    return this.status;
  }

  isReady(): boolean {
    return false;
  }

  async generateKeyPair(_request: KeyPairGenerationRequest): Promise<KeyPairProviderResult | null> {
    return null;
  }

  async deriveAddress(
    _keyPairId: KeyPairId,
    _addressType: string,
    _derivationPath?: string
  ): Promise<AddressProviderResult | null> {
    return null;
  }

  async signMessage(
    _keyPairId: KeyPairId,
    _messageHash: MessageHashHex,
    _algorithm: CryptoAlgorithm
  ): Promise<SignatureProviderResult | null> {
    return null;
  }

  async verifySignature(
    _messageHash: MessageHashHex,
    _signature: string,
    _publicKey: PublicKeyHex,
    _algorithm: CryptoAlgorithm
  ): Promise<boolean> {
    return false;
  }

  async validatePublicKey(
    _publicKey: PublicKeyHex,
    _algorithm: CryptoAlgorithm,
    _curve: EllipticCurve
  ): Promise<boolean> {
    return false;
  }

  async rotateKey(_keyPairId: KeyPairId): Promise<KeyPairProviderResult | null> {
    return null;
  }

  async revokeKey(_keyPairId: KeyPairId): Promise<boolean> {
    return false;
  }

  async getKeyPair(_keyPairId: KeyPairId): Promise<RTWOKeyPair | null> {
    return null;
  }

  getDescriptor(): RTWOKeyProviderDescriptor {
    return {
      providerId: this.providerId,
      providerName: this.providerName,
      providerType: this.providerType,
      status: this.status,
      isConfigured: this.providerType !== 'not_configured',
      configuredAt: null,
      description: this.scaffoldMessage,
    };
  }
}
